import { useEffect, useRef } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import { FractalCanvas } from './FractalCanvas';

// Register only JavaScript for smaller bundle size
hljs.registerLanguage('javascript', javascript);

// Render LaTeX equations using bundled KaTeX (works in production/file://)
const Equation = ({ tex }) => {
  const ref = useRef(null);
  
  useEffect(() => {
    if (ref.current) {
      try {
        katex.render(tex, ref.current, {
          throwOnError: false,
          displayMode: true
        });
      } catch (e) {
        console.error('KaTeX render error:', e);
        ref.current.textContent = tex;
      }
    }
  }, [tex]);
  
  return <div className="equation" ref={ref}>{tex}</div>;
}

const CodeBlock = ({ code }) => {
  const trimmed = code.trim();
  const { value: highlighted } = hljs.highlight(trimmed, { language: 'javascript' });
  return (
    <pre>
      <code
        className="language-javascript hljs"
        dangerouslySetInnerHTML={{ __html: highlighted }}
      />
    </pre>
  );
};

// Blue (low mu) -> yellow (high mu); used by WebGL, worker, and 2D canvas
function blueYellow(mu, gScale = 7, aScale = 15) {
  const t = Math.min(1, (mu * gScale) / 255);
  return [
    Math.round(255 * t),
    Math.round(255 * t),
    Math.round(255 * (1 - t)),
    Math.min(255, Math.round(mu * aScale))
  ];
}

const App = () => {
  const preferredRenderer = 'webgl';

  return <>
  <header>
    <h1>The Mandelbrot Set</h1>
  </header>

  <section className="section" data-section="intro">
  <div className="container">
    <p>
      If we plot the escape times for points in this complex plane series,
      we end up with one of the most famous visualizations in mathematics.
    </p>
    <Equation tex="z_{n+1} = z_n^2 + c, \quad{i = \sqrt{-1}}" />
    <p>
      The main cardioid is centered near the origin, with the period-2
      bulb to its left at about &minus;1.
    </p>
    <FractalCanvas
      width={1600}
      height={1600}
      xRange={[-2.2, 0.8]}
      yRange={[-1.5, 1.5]}
      colorFunc={({ mu }) => blueYellow(mu, 7, 15)}
      preferredRenderer={preferredRenderer}
    />
  </div>
  </section>

  <section className="section" data-section="world-overview">
  <div className="container">
    <h3>World overview</h3>
    <p>
      If we consider an escape radius of 2, the world also has a radius
      of 2, since any points starting outside of the world diverge right away.
    </p>
    <FractalCanvas
      width={800}
      height={800}
      xRange={[-2.2, 2.2]}
      yRange={[-2.2, 2.2]}
      colorFunc={({ mu }) => blueYellow(mu, 7, 15)}
      preferredRenderer={preferredRenderer}
    />

    <p>
      The set lies roughly on the real axis between &minus;2 and 0.5, with
      the imaginary extent reaching about ±1.25.
    </p>
    <FractalCanvas
      width={800}
      height={600}
      xRange={[-2.2, 0.8]}
      yRange={[-1.25, 1.25]}
      colorFunc={({ mu }) => blueYellow(mu, 7, 15)}
      preferredRenderer={preferredRenderer}
    />
  </div>
  </section>

  <section className="section" data-section="equation">
  <div className="container">
    <h3>Mandelbrot set equation</h3>

    <p>The equation using complex numbers is:</p>
    <Equation tex="z_{n+1} = z_n^2 + c" />

    <p>z is a complex number composed of real and imaginary parts:</p>
    <Equation tex="z = x + iy, \quad{i = \sqrt{-1}}" />

    <p>Fractal images are generated by varying the value of c:</p>
    <Equation tex="c = (x_0, y_0)" />

    <p>If we expand the complex squaring, the iteration reduces to:</p>
    <Equation tex="x_{n+1} = x_n^2 - y_n^2 + x_0" />
    <Equation tex="y_{n+1} = 2x_ny_n + y_0" />

    <section>
      <p>
        A common way of exploring fractals is by putting different starting (x0, y0)
        numbers into the equation and making note of whether the series:
      </p>
      <ul>
        <li>diverges to infinity or stays within a limit</li>
        <li>if the series diverges, we make note of:
          <ul>
            <li>the number of iterations until reaching the escape radius</li>
            <li>the distance from zero (0, 0) during the escape</li>
          </ul>
        </li>
      </ul>
    </section>
  </div>
  </section>

  <section className="section" data-section="coloring">
  <div className="container">
    <h3>Fractal coloring</h3>

    <p>
      Escape time: given a starting point (x0, y0), we can give each
      point a color by calculating the number of iterations it takes
      for the series to diverge, and the distance from zero when escaping.
    </p>
    <CodeBlock code={`
const ESCAPE_THRESHOLD = 4;
const MAX_ITERATIONS = 255;

function iterateUntilEscape(x0, y0) {
  let x = 0, y = 0;
  let numIterations = 0;
  while ((x*x + y*y < ESCAPE_THRESHOLD) &&
         (numIterations < MAX_ITERATIONS)) {
    const x_next = x*x - y*y + x0;
    const y_next = 2*x*y + y0;
    x = x_next;
    y = y_next;
    numIterations++;
  }
  return [numIterations, x*x + y*y];
}
`}/>
  </div>
  </section>

  <section className="section" data-section="implementation">
  <div className="container">
    <p>
      We'll set the RGBA colors of every pixel in the image based
      on the number of iterations. Here's an example of a fiery yellow/red
      color palette by scaling just the green and alpha values of each pixel.
    </p>
    <CodeBlock code={`
function getColor(numIterations) {
  return [
    255,                // red
    numIterations * 7,  // green
    0,                  // blue
    numIterations * 15  // alpha
  ];
}
`}/>

    <p>
      Here's a general function for drawing fractals onto canvas elements.
      Changing the iterateUntilEscape function will change the drawn fractal.
    </p>

    <CodeBlock code={`
function drawFractal(canvasEl, xRange, yRange, getColor) {
  const canvasWidth = canvasEl.width;
  const canvasHeight = canvasEl.height;
  const context = canvasEl.getContext('2d');
  const canvasImageData =
    context.createImageData(canvasWidth, canvasHeight);
  const image = canvasImageData.data;
  for (let i = 0; i < canvasHeight; i++) {
    for (let j = 0; j < canvasWidth; j++) {
      const x0 = xRange[0] +
        j*(xRange[1] - xRange[0]) / canvasWidth;
      const y0 = yRange[0] +
        i*(yRange[1] - yRange[0]) / canvasHeight;
      const [numIterations, escapeDistance] =
        iterateUntilEscape(x0, y0);
      const pixelIndex = 4*i*canvasWidth + 4*j;
      if (numIterations !== MAX_ITERATIONS) {
        const mu = getMu(numIterations, escapeDistance);
        const pixels = getColor({ numIterations, mu });
        for (let p = 0; p < 4; p++) {
          image[pixelIndex + p] = pixels[p];
        }
      }
    }
  }
  context.putImageData(canvasImageData, 0, 0);
}`
}/>

    <p>
      Here's the main cardioid colored based on
      number of iterations alone.
    </p>
    <FractalCanvas
      width={1600}
      height={1600}
      xRange={[-2.2, 0.8]}
      yRange={[-1.5, 1.5]}
      colorFunc={({ numIterations }) => blueYellow(numIterations, 7, 15)}
      preferredRenderer={preferredRenderer}
    />
    <p>
      By considering escape distances on a logarithmic scale, we
      can use this extra information to smooth the color transitions.
    </p>
    <CodeBlock code={`
const ESCAPE_RADIUS = 2;

function getMu(numIterations, escapeDistance) {
  return numIterations + 1 -
    Math.log(escapeDistance) / Math.log(ESCAPE_RADIUS);
}

function getColor(mu) {
  return [ 255, mu * 7, 0, mu * 15 ];
}
`}/>
    <p>
      Here's the same image as above colored with renormalized iteration counts.
    </p>
    <FractalCanvas
      width={1600}
      height={1600}
      xRange={[-2.2, 0.8]}
      yRange={[-1.5, 1.5]}
      colorFunc={({ mu }) => blueYellow(mu, 7, 15)}
      preferredRenderer={preferredRenderer}
    />
    <p>
      Nebulabrots are a way of rendering fractals where pixels are colored based
      on how frequently they're visited while calculating the iterations until
      escape across the viewport.
    </p>
  </div>
  </section>

  <section className="section" data-section="substructures">
  <div className="container">
    <h3>Sub-structures</h3>
    <p>
      Zoomed-in to various parts of the Mandelbrot world.
    </p>

    <p>
      Seahorse Valley &mdash; the region between the main cardioid
      and the period-2 bulb, centred near &minus;0.75 + 0.1i.
    </p>
    <FractalCanvas
      width={1600}
      height={1200}
      xRange={[-0.79, -0.71]}
      yRange={[0.06, 0.14]}
      colorFunc={({ mu }) => blueYellow(mu, 7, 15)}
      preferredRenderer={preferredRenderer}
    />

    <p>
      Elephant Valley &mdash; the region near 0.3 on the real axis,
      with trunk-like spiral structures.
    </p>
    <FractalCanvas
      width={1600}
      height={1200}
      xRange={[0.25, 0.35]}
      yRange={[-0.05, 0.05]}
      colorFunc={({ mu }) => blueYellow(mu, 7, 15)}
      preferredRenderer={preferredRenderer}
    />

    <p>
      The tip of the antenna at &minus;2.0 &mdash; Misiurewicz point.
    </p>
    <FractalCanvas
      width={1600}
      height={800}
      xRange={[-2.01, -1.9469]}
      yRange={[-0.02, 0.02]}
      colorFunc={({ mu }) => blueYellow(mu, 12, 30)}
      preferredRenderer={preferredRenderer}
    />
  </div>
  </section>

  <section className="section" data-section="hero-canvas">
    <FractalCanvas
      width={1920}
      height={1500}
      xRange={[-2.5, 1.0]}
      yRange={[-1.37, 1.37]}
      colorFunc={({ mu }) => blueYellow(mu, 7, 15)}
      preferredRenderer={preferredRenderer}
      showRendererToggle={true}
    />
  </section>
</>;
};

export default App;
